<template>
  <div class="container">
    <!-- Success and Error Messages -->
    <div v-if="successMessage" class="alert alert-success alert-dismissible fade show fixed-alert" role="alert">
      {{ successMessage }}
      <button type="button" class="btn-close" @click="successMessage = ''"></button>
    </div>

    <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show fixed-alert" role="alert">
      {{ errorMessage }}
      <button type="button" class="btn-close" @click="errorMessage = ''"></button>
    </div>

    <!-- Page Title -->
    <div class="mt-4">
      <h2>{{ $t('holidayTitle') }}</h2>
    </div>

    <!-- Search Input -->
    <div class="mb-4">
      <input
          v-model="query"
          @input="searchHolidays"
          class="form-control"
          type="text"
          placeholder="Search holidays..."
      />
    </div>

    <!-- Add Holiday Button -->
    <div class="mb-4">
      <button class="btn btn-primary" @click="openModal('add')">
        <i class="bi bi-plus-circle"></i> {{ $t('addHoliday') }}
      </button>
    </div>

    <!-- Pagination Buttons -->
    <div class="pagination-controls">
      <button
          class="btn pagination-btn"
          @click="goToPreviousPage()"
          v-if="showPreviousButton"
      >
        <i class="bi bi-arrow-left"></i>
        {{ $t('previous') }}
      </button>
      <button
          class="btn pagination-btn"
          @click="goToNextPage()"
          v-if="showNextButton"
      >
        {{ $t('next') }}
        <i class="bi bi-arrow-right"></i>
      </button>
    </div>

    <!-- Holidays List Table -->
    <div v-if="holidays.length > 0">
      <table class="table table-hover">
        <thead>
        <tr>
          <th>{{ $t('ID') }}</th>
          <th>{{ $t('date') }}</th>
          <th>{{ $t('name') }}</th>
          <th>{{ $t('year') }}</th>
          <th>{{ $t('actions') }}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="holiday in holidays" :key="holiday.id">
          <td>{{ holiday.id }}</td>
          <td>{{ formatDate(holiday.date) }}</td>
          <td>{{ holiday.name }}</td>
          <td>{{ holiday.year_label }}</td>
          <td>
            <button class="btn btn-primary btn-sm" @click="openModal('edit', holiday)">
              <i class="bi bi-pencil-square"></i> {{ $t('edit') }}
            </button>
            <button class="btn btn-danger btn-sm" @click="openConfirmDelete(holiday.id)">
              <i class="bi bi-trash"></i> {{ $t('delete') }}
            </button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="alert alert-warning" role="alert">
      {{ $t('noHolidays') }}
    </div>

    <!-- Modal for Add/Edit Holiday -->
    <div class="modal fade" id="holidayModal" tabindex="-1" aria-labelledby="holidayModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="holidayModalLabel">{{ isEditMode ? $t('editHoliday') : $t('addHoliday') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitHoliday">
              <div class="mb-3">
                <label for="dateInput" class="form-label">{{ $t('date') }}</label>
                <input v-model="holidayForm.date" type="date" class="form-control" id="dateInput" required/>
              </div>
              <div class="mb-3">
                <label for="nameInput" class="form-label">{{ $t('name') }}</label>
                <input v-model="holidayForm.name" type="text" class="form-control" id="nameInput" required/>
              </div>
              <div class="mb-3">
                <label for="yearSelect" class="form-label">{{ $t('year') }}</label>
                <select v-model="holidayForm.year" class="form-select" id="yearSelect" required>
                  <option v-for="year in years" :key="year.id" :value="year.id">
                    {{ year.year }}
                  </option>
                </select>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('cancel') }}</button>
            <button type="button" class="btn btn-primary" @click="submitHoliday">
              {{ isEditMode ? $t('update') : $t('add') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm Delete Modal -->
    <div v-if="showConfirm" class="modal-overlay">
      <div class="modal-container">
        <h3 class="modal-title">{{ $t('confirmDeleteTitle') }}</h3>
        <p>{{ $t('confirmDeleteHoliday') }}</p>
        <div class="modal-actions">
          <button class="btn btn-danger" @click="deleteHoliday">{{ $t('confirmDelete') }}</button>
          <button class="btn btn-primary" @click="cancelDelete">{{ $t('cancel') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "Holidays",
  data() {
    return {
      holidays: [],
      years: [], // Stores available years from YearSetting
      showNextButton: false,
      showPreviousButton: false,
      currentPage: 1,
      query: "", // Search query
      successMessage: "",
      errorMessage: "",
      isEditMode: false,
      holidayForm: {
        id: null,
        date: "",
        name: "",
        year: null, // Selected year ID
      },
      modalInstance: null,
      showConfirm: false,
      holidayIdToDelete: null,
    };
  },
  mounted() {
    this.getHolidays();
    this.getYears(); // Fetch YearSetting data
    this.$nextTick(() => {
      const modalElement = document.getElementById("holidayModal");
      if (modalElement) {
        this.modalInstance = new bootstrap.Modal(modalElement);
      }
    });
  },
  watch: {
    $route: {
      handler: 'getHolidays',
      immediate: true,
    },
  },
  methods: {
    async getYears() {
      try {
        const response = await axios.get("/api/v1/vacation/year-settings/");
        this.years = response.data.results || response.data;
      } catch (error) {
        this.errorMessage = "Failed to fetch years.";
        setTimeout(() => {
          this.errorMessage = "";
        }, 3000);
      }
    },
    openModal(mode, holiday = null) {
      if (mode === "add") {
        this.isEditMode = false;
        this.holidayForm = {id: null, date: "", name: "", year: null};
      } else if (mode === "edit") {
        this.isEditMode = true;
        this.holidayForm = {id: holiday.id, date: holiday.date, name: holiday.name, year: holiday.year.id};
      }
      this.modalInstance.show();
    },
    closeModal() {
      this.modalInstance.hide();
    },
    async submitHoliday() {
      try {
        if (this.isEditMode) {
          await axios.put(`/api/v1/vacation/holidays/${this.holidayForm.id}/`, this.holidayForm);
          this.successMessage = "Holiday updated successfully.";
        } else {
          await axios.post(`/api/v1/vacation/holidays/`, this.holidayForm);
          this.successMessage = "Holiday added successfully.";
        }
        this.getHolidays();
      } catch (error) {
        this.errorMessage = "Failed to save holiday.";
      } finally {
        this.closeModal();
        setTimeout(() => {
          this.successMessage = "";
          this.errorMessage = "";
        }, 3000);
      }
    },
    async deleteHoliday() {
      try {
        await axios.delete(`/api/v1/vacation/holidays/${this.holidayIdToDelete}/`);
        this.successMessage = "Holiday deleted successfully.";
        this.getHolidays();
      } catch (error) {
        this.errorMessage = "Failed to delete holiday.";
      } finally {
        this.showConfirm = false;
        setTimeout(() => {
          this.successMessage = "";
          this.errorMessage = "";
        }, 3000);
      }
    },
    openConfirmDelete(holidayId) {
      this.holidayIdToDelete = holidayId;
      this.showConfirm = true;
    },
    cancelDelete() {
      this.showConfirm = false;
      this.holidayIdToDelete = null;
    },
    async getHolidays() {
      try {
        const params = {
          page: this.currentPage,
          search: this.query,
        };

        // Include year_id from the query parameter if it exists
        if (this.$route.query.year_id) {
          params.year_id = this.$route.query.year_id;
        }

        const response = await axios.get('/api/v1/vacation/holidays/', {params});
        this.holidays = response.data.results || response.data;
        this.showNextButton = Boolean(response.data.next);
        this.showPreviousButton = Boolean(response.data.previous);
      } catch (error) {
        this.errorMessage = 'Failed to fetch holidays.';
        setTimeout(() => {
          this.errorMessage = '';
        }, 3000);
      }
    },
    formatDate(date) {
      return date
          ? new Date(date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
          : "";
    },
    searchHolidays() {
      this.currentPage = 1;
      this.getHolidays();
    },
    goToNextPage() {
      this.currentPage += 1;
      this.getHolidays();
    },
    goToPreviousPage() {
      this.currentPage -= 1;
      this.getHolidays();
    },
  },
};
</script>

<style>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-container {
  background: white;
  padding: 20px;
  border-radius: 8px;
  width: 300px;
}
</style>
